/** @prettier */

import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  IconButton,
} from '@mui/material';
import {useParams} from 'react-router-dom';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';

//MTH component import
import {dayOfWeekTable} from './ScheduleSetting';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
import {SelectTypeMenu} from './SelectTypeMenuList';
import {iconBlack} from '../../define';
//API import
import {putSiteCalenderRoutine} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';

/**
 * 標準設定（週）編集ダイアログ
 * @param props props
 * - calenderRoutine 標準設定DBデータ
 * - isOpenDialog ダイアログ開閉
 * - setIsOpenDialog ダイアログ開閉設定
 * - setIsRoutineChange 標準設定変更フラグ
 */
export const DayOfWeekSettingDialog = (props: {
  calenderRoutine: apiIF.res.getSiteCalenderRoutine;
  isOpenDialog: boolean;
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setIsRoutineChange: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const trans = useTranslation().t; //翻訳関数
  //パスパラメータ取得
  const {siteID} = useParams();

  /** 標準設定データ */
  const [calenderRoutineEditData, setCalenderRoutineEditData] =
    useState<apiIF.res.getSiteCalenderRoutine>(props.calenderRoutine);
  /** 特定日の設定メニュー開閉用 */
  const [isOpenedMenu, setIsOpenedMenu] = useState(false);
  /** 何曜日を設定したかメニューに渡す */
  const [selectedDayOfWeek, setSelectedDayOfWeek] =
    useState<keyof apiIF.res.getSiteCalenderRoutine['weekly']>('monday');
  /** メニューの出現位置設定用 */
  const [anchorEl, setAnchorEl] = useState(null);
  /** API応答待ちフラグ */
  const [apiWait, setAPIWait] = useState(false);
  /**
   * 曜日設定ダイアログのOKボタン
   */
  const handleOK = () => {
    if (siteID === undefined) {
      return;
    }
    setAPIWait(true);
    //標準設定をDBに保存
    const editData = {
      'weekly': calenderRoutineEditData['weekly'],
      'specified-day': props.calenderRoutine['specified-day'],
    };
    putSiteCalenderRoutine(siteID, editData)
      .then(() => {
        //変更フラグON
        props.setIsRoutineChange(true);
      })
      .finally(() => {
        setAPIWait(false);
      });
    //ダイアログを閉じる
    props.setIsOpenDialog(false);
  };

  /** 曜日設定ダイアログのCANCELボタン */
  const handleCancel = () => {
    // ダイアログ内の表示をもとに戻す
    setCalenderRoutineEditData(props.calenderRoutine);
    //ダイアログを閉じる
    props.setIsOpenDialog(false);
  };

  /**
   * 特定日編集メニューアイテム押下時の処理
   * @param num 選択した特定日タイプメニューのインデックス
   */
  const clickMenuItem = (num: number = -1) => {
    setIsOpenedMenu(false);
    if (num === -1) {
      // 変更内容を指定しなかった場合は変更せず終了する
      return;
    }
    switch (num) {
      case 0:
        setCalenderRoutineEditData({
          ...calenderRoutineEditData,
          weekly: {
            ...calenderRoutineEditData.weekly,
            [selectedDayOfWeek]: 'weekday',
          },
        });
        break;
      case 1:
        setCalenderRoutineEditData({
          ...calenderRoutineEditData,
          weekly: {
            ...calenderRoutineEditData.weekly,
            [selectedDayOfWeek]: 'holiday',
          },
        });
        break;
      case 2:
        setCalenderRoutineEditData({
          ...calenderRoutineEditData,
          weekly: {
            ...calenderRoutineEditData.weekly,
            [selectedDayOfWeek]: 'special-day-1',
          },
        });
        break;
      case 3:
        setCalenderRoutineEditData({
          ...calenderRoutineEditData,
          weekly: {
            ...calenderRoutineEditData.weekly,
            [selectedDayOfWeek]: 'special-day-2',
          },
        });
        break;
    }
  };

  /**
   * 下矢印から特定日の設定メニューを開く
   * @param {React.MouseEvent} event マウスイベント
   * @param {number} dayOfWeekIndex 曜日のインデックス（0:月、1：火～6:日）
   */
  const clickArrowDropDown = (
    event: React.MouseEvent,
    dayOfWeekIndex: number
  ) => {
    setIsOpenedMenu(true);
    setAnchorEl(event.currentTarget as unknown as React.SetStateAction<null>);
    setSelectedDayOfWeek(dayOfWeekTable[dayOfWeekIndex]);
  };

  return (
    <>
      {/* 標準曜日設定ダイアログ*/}
      <Dialog
        open={props.isOpenDialog}
        onClose={() => props.setIsOpenDialog(false)}
        maxWidth="md">
        <DialogTitle>{trans('DayOfWeekSettingDialog.title')}</DialogTitle>
        <DialogContent>
          {/* 設定用のテーブル */}
          <Grid container>
            {dayOfWeekTable.map((value, index) => {
              /** 適用するクラス名 */
              const className = ((
                dayType:
                  | 'weekday'
                  | 'holiday'
                  | 'special-day-1'
                  | 'special-day-2'
              ) => {
                switch (dayType) {
                  case 'weekday':
                    return 'schedule__dayOfWeekSettingDialog__item--weekday';
                  case 'holiday':
                    return 'schedule__dayOfWeekSettingDialog__item--holiday';
                  case 'special-day-1':
                    return 'schedule__dayOfWeekSettingDialog__item--specialDay1';
                  case 'special-day-2':
                    return 'schedule__dayOfWeekSettingDialog__item--specialDay2';
                }
              })(calenderRoutineEditData['weekly'][value]);
              return (
                <Grid key={value} item xs={12 / 7} className={className}>
                  <Stack alignItems="center">
                    <Box className="schedule__dayOfWeekSettingDialog__item__text">
                      {trans(`DayOfWeekSettingDialog.dayOfWeek.${value}`)}
                    </Box>
                    {/* 選択メニューを出すボタン */}
                    <IconButton onClick={e => clickArrowDropDown(e, index)}>
                      <img
                        src={iconBlack.CHEVRON_DOWN.src}
                        alt={`${trans(
                          `DayOfWeekSettingDialog.image.alternativeText.${iconBlack.CHEVRON_DOWN.alt}`
                        )}`}
                      />
                    </IconButton>
                  </Stack>
                </Grid>
              );
            })}
          </Grid>
          {/* 特定日タイプ選択用のメニュー */}
          <SelectTypeMenu
            id="weekday-setting-menu"
            menuPosition={anchorEl}
            open={isOpenedMenu}
            clickMenuItem={clickMenuItem}
          />
        </DialogContent>
        <DialogActions>
          {/* Cancel Button */}
          <SecondaryButton
            onClick={handleCancel}
            buttonText={trans('DayOfWeekSettingDialog.buttonTxt.cancel')}
            disabled={apiWait}
          />
          {/* OK Button */}
          <PrimaryButton
            onClick={handleOK}
            buttonText={trans('DayOfWeekSettingDialog.buttonTxt.ok')}
            disabled={apiWait}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};
